import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ViewStyle,
} from 'react-native';
import { DayItem } from '../DLOFilterByDayModal';

export interface DLOCreateActualHoursTableActionsHeaderProps {
  style?: ViewStyle;
  isChecked?: boolean;
  onCheckboxPress?: () => void;
  selectedDays?: string[];
  onDayFilterPress?: (day: string) => void;
  dayFilter?: DayItem[];
  inputsEnabled?: boolean;
  disabledDays?: string[]; // <-- Add this prop
}

const DLOCreateActualHoursTableActionsHeader: React.FC<DLOCreateActualHoursTableActionsHeaderProps> = ({
  style,
  isChecked = false,
  onCheckboxPress,
  selectedDays = [],
  onDayFilterPress,
  dayFilter = [],
  inputsEnabled = true,
  disabledDays = [], // <-- Add to destructure
}) => {
  const days = [
    { key: 'sun', label: 'Sun' },
    { key: 'mon', label: 'Mon' },
    { key: 'tue', label: 'Tue' },
    { key: 'wed', label: 'Wed' },
    { key: 'thu', label: 'Thu' },
    { key: 'fri', label: 'Fri' },
    { key: 'sat', label: 'Sat' },
  ];

  // Check if a day is disabled based on day filter or disabledDays
  const isDayDisabled = (dayKey: string): boolean => {
    // Map short day keys to full day names
    const dayNameMap: { [key: string]: string } = {
      'sun': 'sunday',
      'mon': 'monday', 
      'tue': 'tuesday',
      'wed': 'wednesday',
      'thu': 'thursday',
      'fri': 'friday',
      'sat': 'saturday'
    };
    const fullDayName = dayNameMap[dayKey];
    if (!fullDayName) return false;
    if (disabledDays.includes(fullDayName)) return true;
    const dayItem = dayFilter.find(item => item.name.toLowerCase() === fullDayName);
    return dayItem ? !dayItem.enabled : false;
  };

  const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <View style={[styles.checkboxContainer, checked && styles.checkboxChecked]}>
      <View style={[styles.checkbox, checked && styles.checkboxInner]}>
        {checked && <Text style={styles.checkmark}>✓</Text>}
      </View>
    </View>
  );

  const DayFilterButton = ({ day, isSelected }: { day: { key: string; label: string }; isSelected: boolean }) => {
    const dayFilterDisabled = isDayDisabled(day.key);
    const disabled = dayFilterDisabled || !inputsEnabled; // Disable if day filter disabled OR inputs disabled
    
    return (
      <TouchableOpacity
        style={[
          styles.dayFilterButton, 
          isSelected && styles.dayFilterButtonSelected,
          disabled && styles.dayFilterButtonDisabled
        ]}
        onPress={disabled ? undefined : () => onDayFilterPress?.(day.key)}
        activeOpacity={disabled ? 1 : 0.8}
        disabled={disabled}
      >
        <Text style={[
          styles.dayFilterText, 
          isSelected && styles.dayFilterTextSelected,
          disabled && styles.dayFilterTextDisabled
        ]}>
          {day.label}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <View style={[styles.container, style]}>
      {/* Checkbox Section */}
      <View style={styles.checkboxSection}>
        <TouchableOpacity onPress={onCheckboxPress} activeOpacity={0.8}>
          <CheckboxIcon checked={isChecked} />
        </TouchableOpacity>
        <View style={styles.verticalDivider} />
      </View>

      {/* Employee Column */}
      <View style={styles.employeeSection}>
        <Text style={styles.columnLabel}>Employee</Text>
      </View>

      {/* Role and Shift Sections */}
      <View style={styles.roleShiftSection}>
        <View style={styles.verticalDivider} />
        <View style={styles.columnContainer}>
          <Text style={styles.columnLabel}>Role</Text>
        </View>
        <View style={styles.verticalDivider} />
        <View style={styles.columnContainer}>
          <Text style={styles.columnLabel}>Shift</Text>
        </View>
      </View>

      {/* Days Filter Section */}
      <View style={styles.daysSection}>
        {days.map((day, index) => (
          <React.Fragment key={day.key}>
            <View style={styles.verticalDivider} />
            <View style={styles.dayContainer}>
              <DayFilterButton
                day={day}
                isSelected={selectedDays.includes(day.key)}
              />
            </View>
          </React.Fragment>
        ))}
        <View style={styles.verticalDivider} />
        
        {/* Total Actual Hours Section (was Total Hours) */}
        <View style={styles.totalHoursContainer}>
          <Text style={styles.columnLabel}>Total Actual Hours</Text>
        </View>

        {/* Schedule Variance Section - Needed for Actual Hours */}
        <View style={styles.verticalDivider} />
        <View style={styles.scheduleVarianceContainer}>
          <Text style={styles.columnLabel}>Schedule Variance</Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 1416,
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0F8FF', // Slightly different background for Actual Hours
    borderWidth: 1,
    borderColor: '#DFDFDF',
  },
  // Checkbox Section
  checkboxSection: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 48,
  },
  checkboxContainer: {
    width: 43,
    height: 48,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  checkbox: {
    width: 12,
    height: 12,
    borderWidth: 1,
    borderColor: '#3A3A3A',
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  checkboxChecked: {
    // Styles for checked state if needed
  },
  checkboxInner: {
    backgroundColor: '#007ACC', // Different color for Actual Hours
    borderColor: '#007ACC',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
  },
  // Employee Section
  employeeSection: {
    width: 183,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Role and Shift Section
  roleShiftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 48,
  },
  columnContainer: {
    width: 165,
    height: 48,
    paddingHorizontal: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  columnLabel: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  // Days Section
  daysSection: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 48,
    gap: 8,
  },
  dayContainer: {
    width: 76,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dayFilterButton: {
    width: 74,
    height: 24,
    paddingHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#D0E7FF', // Different color for Actual Hours
    backgroundColor: '#FBFCFE',
  },
  dayFilterButtonSelected: {
    backgroundColor: '#007ACC', // Different color for Actual Hours
    borderColor: '#007ACC',
  },
  dayFilterButtonDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#E0E0E0',
    opacity: 0.6,
  },
  dayFilterText: {
    color: '#007ACC', // Different color for Actual Hours
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    textAlign: 'center',
  },
  dayFilterTextSelected: {
    color: '#FFFFFF',
  },
  dayFilterTextDisabled: {
    color: '#999999',
  },
  // Total Hours Section
  totalHoursContainer: {
    width: 84,
    height: 48,
    paddingHorizontal: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Schedule Variance Section
  scheduleVarianceContainer: {
    width: 84,
    height: 48,
    paddingHorizontal: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Dividers
  verticalDivider: {
    width: 1,
    height: 48,
    backgroundColor: '#DFDFDF',
  },
});

export default DLOCreateActualHoursTableActionsHeader;